/**
 * Designer API - profile CRUD, versioning, review workflow, and public profile.
 * Uses getApiBaseUrl from api.js and Bearer token for auth.
 */

import { getApiBaseUrl, getBaseHeaders } from "./api";

/**
 * @param {{ accessToken?: string }} options
 * @returns {Headers}
 */
function getHeaders({ accessToken } = {}) {
    const headers = { ...getBaseHeaders() };
    if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
    }
    return headers;
}

/* ------------------------------------------------------------------ */
/*  Profile CRUD                                                      */
/* ------------------------------------------------------------------ */

/**
 * GET /api/designer/profiles - list all profiles for the authenticated user
 */
export async function listProfiles({ accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/designer/profiles`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/designer/profiles/:id - get a single profile
 */
export async function getProfile({ accessToken, id } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/designer/profiles/${id}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * POST /api/designer/profiles - create a new profile
 */
export async function createProfile({
    accessToken,
    name,
    slug,
    settingsJson,
    contentHtml,
    contentPlain,
    contentMarkdown,
    editorState,
} = {}) {
    const base = getApiBaseUrl();
    const body = { name };
    if (slug !== undefined) {
        body.slug = slug;
    }
    if (settingsJson !== undefined) {
        body.settingsJson = settingsJson;
    }
    if (contentHtml !== undefined) {
        body.contentHtml = contentHtml;
    }
    if (contentPlain !== undefined) {
        body.contentPlain = contentPlain;
    }
    if (contentMarkdown !== undefined) {
        body.contentMarkdown = contentMarkdown;
    }
    if (editorState !== undefined) {
        body.editorState = editorState;
    }

    const res = await fetch(`${base}/api/designer/profiles`, {
        method: "POST",
        headers: getHeaders({ accessToken }),
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * PATCH /api/designer/profiles/:id - update a profile (draft fields)
 */
export async function updateProfile({
    accessToken,
    id,
    name,
    slug,
    isDefault,
    settingsJson,
    contentHtml,
    contentPlain,
    contentMarkdown,
    editorState,
} = {}) {
    const base = getApiBaseUrl();
    const body = {};
    if (name !== undefined) {
        body.name = name;
    }
    if (slug !== undefined) {
        body.slug = slug;
    }
    if (isDefault !== undefined) {
        body.isDefault = isDefault;
    }
    if (settingsJson !== undefined) {
        body.settingsJson = settingsJson;
    }
    if (contentHtml !== undefined) {
        body.contentHtml = contentHtml;
    }
    if (contentPlain !== undefined) {
        body.contentPlain = contentPlain;
    }
    if (contentMarkdown !== undefined) {
        body.contentMarkdown = contentMarkdown;
    }
    if (editorState !== undefined) {
        body.editorState = editorState;
    }

    const res = await fetch(`${base}/api/designer/profiles/${id}`, {
        method: "PATCH",
        headers: getHeaders({ accessToken }),
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * DELETE /api/designer/profiles/:id - delete a profile
 */
export async function deleteProfile({ accessToken, id } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/designer/profiles/${id}`, {
        method: "DELETE",
        headers: getHeaders({ accessToken }),
    });
    if (res.status === 204) {
        return { ok: true };
    }
    const json = await res.json().catch(() => ({}));
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/* ------------------------------------------------------------------ */
/*  Review Workflow                                                    */
/* ------------------------------------------------------------------ */

/**
 * POST /api/designer/profiles/:id/submit-review - submit profile for review
 * Creates a new version (snapshot of current draft) and sets status to pending_review.
 */
export async function submitForReview({ accessToken, id } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(
        `${base}/api/designer/profiles/${id}/submit-review`,
        {
            method: "POST",
            headers: getHeaders({ accessToken }),
        },
    );
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * POST /api/designer/profiles/:id/approve - approve a pending version
 */
export async function approveProfile({ accessToken, id, versionId } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/designer/profiles/${id}/approve`, {
        method: "POST",
        headers: getHeaders({ accessToken }),
        body: JSON.stringify({ versionId }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * POST /api/designer/profiles/:id/reject - reject a pending version
 */
export async function rejectProfile({ accessToken, id, versionId, note } = {}) {
    const base = getApiBaseUrl();
    const body = { versionId };
    if (note) {
        body.note = note;
    }
    const res = await fetch(`${base}/api/designer/profiles/${id}/reject`, {
        method: "POST",
        headers: getHeaders({ accessToken }),
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/* ------------------------------------------------------------------ */
/*  Visibility & Versions                                             */
/* ------------------------------------------------------------------ */

/**
 * PATCH /api/designer/profiles/:id/visibility - toggle public visibility
 */
export async function toggleVisibility({ accessToken, id, isPublic } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/designer/profiles/${id}/visibility`, {
        method: "PATCH",
        headers: getHeaders({ accessToken }),
        body: JSON.stringify({ isPublic }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/designer/profiles/:id/versions - get version history
 */
export async function getVersionHistory({ accessToken, id } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/designer/profiles/${id}/versions`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/* ------------------------------------------------------------------ */
/*  Public Profile (No auth required)                                 */
/* ------------------------------------------------------------------ */

/**
 * GET /api/p/check/:slug - check if profile slug exists (public, for registration)
 * @returns {{ ok: boolean, data?: { exists: boolean }, error?: string, networkError?: boolean }}
 */
export async function checkProfileSlugExists({ slug } = {}) {
    try {
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/api/p/check/${encodeURIComponent(slug || "")}`, {
            method: "GET",
            headers: getBaseHeaders(),
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok && json.success === true && json.data) {
            return { ok: true, data: json.data };
        }
        return {
            ok: false,
            error: json?.error?.message || `Request failed (${res.status})`,
        };
    } catch (err) {
        if (err instanceof TypeError && err.message === "Failed to fetch") {
            return {
                ok: false,
                error: "Unable to connect to server. Please ensure the backend is running.",
                networkError: true,
            };
        }
        return { ok: false, error: err?.message || "Network error", networkError: true };
    }
}

/**
 * GET /api/p/:slug - fetch public profile (solo.to style: mydomain/slug)
 * Optionally pass accessToken so authenticated owners can preview their own unpublished profiles.
 */
export async function getPublicProfile({ slug, accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/p/${encodeURIComponent(slug || "")}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/* ------------------------------------------------------------------ */
/*  Persistent visitor & session IDs                                  */
/* ------------------------------------------------------------------ */
function uuid() {
    // crypto.randomUUID is widely supported; fallback to Math.random
    if (typeof crypto !== "undefined" && crypto.randomUUID)
        return crypto.randomUUID();
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
}

/** Visitor ID persists across sessions (localStorage). */
function getVisitorId() {
    const KEY = "oi_vid";
    try {
        let vid = localStorage.getItem(KEY);
        if (!vid) {
            vid = uuid();
            localStorage.setItem(KEY, vid);
        }
        return vid;
    } catch {
        return uuid();
    }
}

/** Session ID resets when the tab/browser closes (sessionStorage). */
function getSessionId() {
    const KEY = "oi_sid";
    try {
        let sid = sessionStorage.getItem(KEY);
        if (!sid) {
            sid = uuid();
            sessionStorage.setItem(KEY, sid);
        }
        return sid;
    } catch {
        return uuid();
    }
}

/* ------------------------------------------------------------------ */
/*  IP-based geolocation (async, always fresh)                        */
/*  Uses a free IP-geo API so location reflects the browser's public  */
/*  IP (respects VPN / proxy). Falls back to timezone mapping.        */
/*                                                                    */
/*  NOTE: VPN does NOT change the OS timezone — timezone will always  */
/*  be the system's local zone (e.g. Asia/Karachi). Only the IP-based */
/*  location (country, region, city, lat, lng) reflects VPN.          */
/* ------------------------------------------------------------------ */
let _ipGeo = {
    country: null,
    region: null,
    city: null,
    lat: null,
    lng: null,
    ip: null,
    fetchedAt: 0,
};
let _ipGeoFetching = false; // prevents concurrent fetches

/**
 * Fetch IP geolocation from ipapi.co.
 * - Always fetches fresh on every page load (no sessionStorage cache).
 * - Stores the public IP so we can detect VPN changes within the same session.
 * - Re-fetches if public IP changed (checked via periodic refreshIpGeo calls).
 */
function requestIpGeolocation() {
    if (_ipGeoFetching) return;
    _ipGeoFetching = true;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    const base = getApiBaseUrl();
    fetch(`${base}/api/ip`, { signal: controller.signal })
        .then((res) => {
            clearTimeout(timeoutId);
            return res.json();
        })
        .then((json) => {
            _ipGeoFetching = false;
            if (json && json.country_code) {
                const newIp = json.ip || null;
                const ipChanged = _ipGeo.ip && newIp && _ipGeo.ip !== newIp;

                _ipGeo.country = json.country_code;
                _ipGeo.region = json.region || null;
                _ipGeo.city = json.city || null;
                _ipGeo.lat =
                    typeof json.latitude === "number" ? json.latitude : null;
                _ipGeo.lng =
                    typeof json.longitude === "number" ? json.longitude : null;
                _ipGeo.ip = newIp;
                _ipGeo.fetchedAt = Date.now();

                // If IP changed (VPN toggled), invalidate cached device context so next
                // trackEvent picks up the new location immediately.
                if (ipChanged) {
                    _deviceCtx = null;
                    _deviceCtxGeoSource = null;
                }
            }
        })
        .catch(() => {
            _ipGeoFetching = false;
            /* API failed — timezone fallback will be used */
        });
}

/**
 * Periodically re-check IP geo (called before each trackEvent).
 * Only re-fetches if the last fetch was > 2 minutes ago — detects VPN connect/disconnect.
 */
const IP_GEO_REFRESH_INTERVAL = 2 * 60 * 1000; // 2 minutes
function refreshIpGeoIfStale() {
    if (Date.now() - _ipGeo.fetchedAt > IP_GEO_REFRESH_INTERVAL) {
        requestIpGeolocation();
    }
}

// Fire immediately on import so result is ready by the time first trackEvent fires
requestIpGeolocation();

/* ------------------------------------------------------------------ */
/*  Rich device / browser context – collected once, cached            */
/* ------------------------------------------------------------------ */
let _deviceCtx = null;
let _deviceCtxGeoSource = null; // tracks which geo source was used ('ip' | 'tz' | 'locale')

function getDeviceContext() {
    // Re-build context if:
    // 1. IP geo resolved after initial cache (upgrade from timezone fallback to IP-based)
    // 2. IP geo data was refreshed (VPN connect/disconnect detected)
    if (_deviceCtx && _deviceCtxGeoSource !== "ip" && _ipGeo.country) {
        _deviceCtx = null; // force rebuild with IP geo data
    }
    if (
        _deviceCtx &&
        _ipGeo.country &&
        _deviceCtx.countryHint !== _ipGeo.country
    ) {
        _deviceCtx = null; // IP changed (VPN toggled) — rebuild
    }
    if (_deviceCtx) return _deviceCtx;
    const nav = typeof navigator !== "undefined" ? navigator : {};
    const win = typeof window !== "undefined" ? window : {};
    const scr = win.screen || {};

    // Timezone → country hint (best-effort mapping for localhost / no-IP scenarios)
    // Timezone → full geo mapping (country, region, city)
    const TZ_GEO_MAP = {
        "Asia/Karachi": { country: "PK", region: "Sindh", city: "Karachi" },
        "Asia/Lahore": { country: "PK", region: "Punjab", city: "Lahore" },
        "Asia/Kolkata": {
            country: "IN",
            region: "Maharashtra",
            city: "Mumbai",
        },
        "Asia/Calcutta": {
            country: "IN",
            region: "West Bengal",
            city: "Kolkata",
        },
        "Asia/Mumbai": { country: "IN", region: "Maharashtra", city: "Mumbai" },
        "America/New_York": {
            country: "US",
            region: "New York",
            city: "New York",
        },
        "America/Chicago": {
            country: "US",
            region: "Illinois",
            city: "Chicago",
        },
        "America/Denver": { country: "US", region: "Colorado", city: "Denver" },
        "America/Los_Angeles": {
            country: "US",
            region: "California",
            city: "Los Angeles",
        },
        "America/Toronto": {
            country: "CA",
            region: "Ontario",
            city: "Toronto",
        },
        "America/Vancouver": {
            country: "CA",
            region: "British Columbia",
            city: "Vancouver",
        },
        "Europe/London": { country: "GB", region: "England", city: "London" },
        "Europe/Paris": {
            country: "FR",
            region: "Île-de-France",
            city: "Paris",
        },
        "Europe/Berlin": { country: "DE", region: "Berlin", city: "Berlin" },
        "Asia/Dubai": { country: "AE", region: "Dubai", city: "Dubai" },
        "Asia/Riyadh": { country: "SA", region: "Riyadh", city: "Riyadh" },
        "Asia/Tokyo": { country: "JP", region: "Tokyo", city: "Tokyo" },
        "Asia/Shanghai": {
            country: "CN",
            region: "Shanghai",
            city: "Shanghai",
        },
        "Asia/Singapore": {
            country: "SG",
            region: "Singapore",
            city: "Singapore",
        },
        "Australia/Sydney": {
            country: "AU",
            region: "New South Wales",
            city: "Sydney",
        },
        "Australia/Melbourne": {
            country: "AU",
            region: "Victoria",
            city: "Melbourne",
        },
        "America/Sao_Paulo": {
            country: "BR",
            region: "São Paulo",
            city: "São Paulo",
        },
        "America/Mexico_City": {
            country: "MX",
            region: "Mexico City",
            city: "Mexico City",
        },
        "Europe/Istanbul": {
            country: "TR",
            region: "Istanbul",
            city: "Istanbul",
        },
        "Europe/Amsterdam": {
            country: "NL",
            region: "North Holland",
            city: "Amsterdam",
        },
        "Europe/Stockholm": {
            country: "SE",
            region: "Stockholm",
            city: "Stockholm",
        },
        "Asia/Jakarta": { country: "ID", region: "Jakarta", city: "Jakarta" },
        "Asia/Kuala_Lumpur": {
            country: "MY",
            region: "Kuala Lumpur",
            city: "Kuala Lumpur",
        },
        "Africa/Johannesburg": {
            country: "ZA",
            region: "Gauteng",
            city: "Johannesburg",
        },
        "Africa/Lagos": { country: "NG", region: "Lagos", city: "Lagos" },
        "Africa/Cairo": { country: "EG", region: "Cairo", city: "Cairo" },
        "Africa/Nairobi": { country: "KE", region: "Nairobi", city: "Nairobi" },
    };

    // Timezone + locale
    let timezone, locale, countryHint, regionHint, cityHint;
    try {
        const opts = Intl.DateTimeFormat().resolvedOptions();
        timezone = opts.timeZone;
        locale = opts.locale;

        // ── PRIMARY: Use IP-based geolocation (respects VPN / proxy) ──
        if (_ipGeo.country) {
            countryHint = _ipGeo.country;
            regionHint = _ipGeo.region;
            cityHint = _ipGeo.city;
            _deviceCtxGeoSource = "ip";
        }

        // ── FALLBACK 1: Derive geo from timezone (if IP-geo didn't resolve yet) ──
        if (!countryHint) {
            const geoMatch = TZ_GEO_MAP[timezone];
            if (geoMatch) {
                countryHint = geoMatch.country;
                regionHint = geoMatch.region;
                cityHint = geoMatch.city;
                _deviceCtxGeoSource = "tz";
            }
        }

        // ── FALLBACK 2: Extract country from locale (e.g. "en-PK" → "PK") ──
        if (!countryHint && locale && locale.includes("-")) {
            const parts = locale.split("-");
            const last = parts[parts.length - 1];
            if (last && last.length === 2 && /^[A-Z]{2}$/.test(last)) {
                countryHint = last;
                _deviceCtxGeoSource = "locale";
            }
        }
    } catch {
        /* ignore */
    }

    // Screen & viewport
    const screenW = scr.width || null;
    const screenH = scr.height || null;
    const viewportW = win.innerWidth || null;
    const viewportH = win.innerHeight || null;
    const pixelRatio = win.devicePixelRatio || null;
    const colorDepth = scr.colorDepth || null;

    // Language
    const language = nav.language || nav.userLanguage || null;
    const languages = nav.languages
        ? Array.from(nav.languages).slice(0, 5)
        : null;

    // Platform
    const platform = nav.userAgentData?.platform || nav.platform || null;

    // Touch / pointer
    const touchPoints = nav.maxTouchPoints ?? null;

    // Connection (Network Information API)
    const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
    const connectionType = conn?.effectiveType || conn?.type || null;
    const connectionDownlink = conn?.downlink || null;

    // Ad / tracking blocker detection (basic)
    // We skip this – it's not reliable and wastes time

    // Do Not Track
    const doNotTrack = nav.doNotTrack === "1" || win.doNotTrack === "1";

    // Cookie enabled
    const cookieEnabled = nav.cookieEnabled ?? null;

    // Hardware concurrency (CPU cores)
    const cpuCores = nav.hardwareConcurrency || null;

    // Device memory (GB, Chrome only)
    const deviceMemory = nav.deviceMemory || null;

    _deviceCtx = {
        timezone,
        locale,
        countryHint,
        regionHint,
        cityHint,
        language,
        languages,
        platform,
        screenResolution: screenW && screenH ? `${screenW}x${screenH}` : null,
        viewportSize:
            viewportW && viewportH ? `${viewportW}x${viewportH}` : null,
        pixelRatio,
        colorDepth,
        touchPoints,
        connectionType,
        connectionDownlink,
        doNotTrack,
        cookieEnabled,
        cpuCores,
        deviceMemory,
    };
    return _deviceCtx;
}

/* ------------------------------------------------------------------ */
/*  UTM parameter extraction                                          */
/* ------------------------------------------------------------------ */
function extractUtmParams() {
    try {
        const params = new URLSearchParams(window.location.search);
        const utm = {};
        for (const key of [
            "utm_source",
            "utm_medium",
            "utm_campaign",
            "utm_term",
            "utm_content",
        ]) {
            const val = params.get(key);
            if (val) utm[key] = val;
        }
        return Object.keys(utm).length > 0 ? utm : null;
    } catch {
        return null;
    }
}

/**
 * POST /api/analytics/track - track analytics event (e.g. pageview) for a profile.
 * No auth required. Call after public profile is loaded to record the view.
 *
 * Automatically attaches rich context:
 *  - Persistent visitorId (localStorage) & sessionId (sessionStorage)
 *  - Device / screen / browser / connection info
 *  - Timezone, locale, language
 *  - UTM parameters (if present in URL)
 */
export async function trackEvent({
    profileId,
    event = "pageview",
    page,
    referrer,
    metadata,
} = {}) {
    const base = getApiBaseUrl();
    // Re-check IP geo if stale (detects VPN connect/disconnect between events)
    refreshIpGeoIfStale();

    // Wait briefly for IP geolocation if it hasn't resolved yet (initial page load race).
    // This ensures the first trackEvent gets accurate VPN-based location instead of
    // falling back to timezone (which doesn't change with VPN).
    if (!_ipGeo.country && _ipGeoFetching) {
        await new Promise((resolve) => {
            let waited = 0;
            const check = setInterval(() => {
                waited += 100;
                if (_ipGeo.country || !_ipGeoFetching || waited >= 3000) {
                    clearInterval(check);
                    resolve();
                }
            }, 100);
        });
    }

    const ctx = getDeviceContext();
    const utm = extractUtmParams();

    const body = {
        profileId,
        event,
        page:
            page ||
            (typeof window !== "undefined" ? window.location?.href : undefined),
        referrer:
            referrer ||
            (typeof document !== "undefined"
                ? document.referrer || undefined
                : undefined),
        metadata: {
            ...(metadata || {}),
            ...(utm ? { utm } : {}),
        },
        // Identification
        visitorId: getVisitorId(),
        sessionId: getSessionId(),
        // Timezone is the OS/system timezone — VPN does NOT change it.
        // (e.g. Asia/Karachi stays even when VPN is connected to US)
        timezone: ctx.timezone,
        // Geo hints come from IP-based geolocation — these DO respect VPN/proxy.
        countryHint: ctx.countryHint,
        regionHint: ctx.regionHint,
        cityHint: ctx.cityHint,
        latHint: _ipGeo.lat,
        lngHint: _ipGeo.lng,
        locale: ctx.locale,
        language: ctx.language,
        languages: ctx.languages,
        // Screen / viewport
        screenResolution: ctx.screenResolution,
        viewportSize: ctx.viewportSize,
        pixelRatio: ctx.pixelRatio,
        colorDepth: ctx.colorDepth,
        // Device capabilities
        platform: ctx.platform,
        touchPoints: ctx.touchPoints,
        cpuCores: ctx.cpuCores,
        deviceMemory: ctx.deviceMemory,
        // Network
        connectionType: ctx.connectionType,
        connectionDownlink: ctx.connectionDownlink,
        // Privacy
        doNotTrack: ctx.doNotTrack,
        cookieEnabled: ctx.cookieEnabled,
    };

    const res = await fetch(`${base}/api/analytics/track`, {
        method: "POST",
        headers: getBaseHeaders(),
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success !== false) {
        return { ok: true };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}
